import { computed, ref, unref, type ComputedRef } from 'vue'
import { useQuery, useQueries, useQueryClient, useMutation } from '@tanstack/vue-query'
import * as Sentry from '@sentry/vue'

import OrganisationManagementService from '@/services/OrganisationManagementService'
import apis from '@/services'
import { useCurrentAccommodation } from '@/composables/useCurrentAccommodation'
import type { Organisation } from '@/types/models'
import type { UserDetailsResponse } from '@/services/OrganisationManagementService'

export interface UpdatePromotionStatusParams {
  promotionType: string
  status: string
}

export const selectedAccommodationId = ref<number | null>(null)

export const setSelectedAccommodationId = (accommodationId: number | null) => {
  if (accommodationId == null) {
    window.localStorage.removeItem('selected_accommodation_id')
  } else {
    window.localStorage.setItem('selected_accommodation_id', String(accommodationId))
  }
  Sentry.setContext('accommodation', {
    id: accommodationId,
  })
  if (window.clarity && accommodationId != null) {
    window.clarity('set', 'selected_accommodation_id', String(accommodationId))
  }
  selectedAccommodationId.value = accommodationId
}

export const fetchUserDetails = async () => {
  const response = await OrganisationManagementService.getUserDetails()
  return response.data
}

export function useUserDetails(userId: ComputedRef<number | null | undefined>) {
  const { isLoading, data, isFetching, isSuccess, isError, fetchStatus } = useQuery({
    enabled: !!userId?.value,
    queryKey: ['userDetails', userId],
    queryFn: () => fetchUserDetails(),
  })

  const fullName = computed(() => {
    if (!data.value?.firstName && !data.value?.lastName) return ''
    return `${data.value.firstName ? data.value.firstName : ''} ${data.value.lastName ? data.value.lastName : ''}`
  })

  const isUserDetailsLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')

  return {
    isUserDetailsFetching: isFetching,
    isUserDetailsLoading,
    isUserDetailsSuccess: isSuccess,
    isUserDetailsError: isError,
    userDetails: data,
    fullName,
  }
}

export const changePromotionStatus = async ({ promotionType, status }: UpdatePromotionStatusParams) => {
  const response = await OrganisationManagementService.updatePromotionStatus({ promotionType, status })
  return response.data
}

export function useUpdatePromotionStatus(userId: ComputedRef<number | null | undefined>) {
  const queryClient = useQueryClient()
  const {
    isPending: isUpdatingPromotionStatus,
    isSuccess: isUpdatingPromotionStatusSuccess,
    isError: isUpdatingPromotionStatusError,
    mutate: updatePromotionStatus,
  } = useMutation({
    mutationFn: ({ promotionType, status }: UpdatePromotionStatusParams) => changePromotionStatus({ promotionType, status }),
    onSuccess: (data, mutationInput) => {
      const userIdValue = unref(userId)
      queryClient.setQueryData<UserDetailsResponse>(['userDetails', userIdValue], oldData => {
        if (!oldData) {
          // No cached data - invalidate to refetch
          queryClient.invalidateQueries({ queryKey: ['userDetails', userIdValue] })
          return oldData
        }

        // If promotions array is empty, invalidate query
        if (!oldData.promotions || oldData.promotions.length === 0) {
          queryClient.invalidateQueries({ queryKey: ['userDetails', userIdValue] })
          return oldData
        }

        // Find the promotion with matching name (mutationInput.promotionType is the
        // same PromotionNameEnum identifier, just a differently-named local param)
        const promotionIndex = oldData.promotions.findIndex(item => item.promotionName === mutationInput.promotionType)

        // If promotion found, update it with response data
        if (promotionIndex !== -1) {
          const updatedPromotions = [...oldData.promotions]
          updatedPromotions[promotionIndex] = {
            ...updatedPromotions[promotionIndex],
            ...data, // Merge response data
          }

          return {
            ...oldData,
            promotions: updatedPromotions,
          }
        }

        // Promotion not found - invalidate to refetch
        queryClient.invalidateQueries({ queryKey: ['userDetails', userIdValue] })
        return oldData
      })
    },
  })

  return {
    isUpdatingPromotionStatus,
    isUpdatingPromotionStatusSuccess,
    isUpdatingPromotionStatusError,
    updatePromotionStatus,
  }
}

export const fetchOrganisationAssignments = async (organisationId: string) => {
  const response = await OrganisationManagementService.getOrganisationAssignments({ organisationId })
  return response.data?.ids ?? []
}

export function useOrganisationAssignments(orgId: ComputedRef<string | null | undefined>) {
  const { isLoading, data, isFetching, isSuccess, isError } = useQuery({
    enabled: !!orgId?.value,
    queryKey: ['organisationAssignments', orgId],
    queryFn: () => {
      const organisationId = unref(orgId)
      if (!organisationId) return []
      return fetchOrganisationAssignments(organisationId)
    },
  })

  return {
    isOrganisationAssignmentsFetching: isFetching,
    isOrganisationAssignmentsLoading: isLoading,
    isOrganisationAssignmentsSuccess: isSuccess,
    isOrganisationAssignmentsError: isError,
    assignmentsData: data,
  }
}

export function useMultipleOrganisationAssignmentsQueries(organisations: ComputedRef<Organisation[] | undefined>) {
  const organisationQueries = computed(() =>
    organisations?.value?.map(org => {
      return {
        queryKey: ['organisationAssignments', org.id],
        queryFn: () => fetchOrganisationAssignments(org.id),
      }
    })
  )
  const organisationAssignmentQueries = useQueries({ queries: organisationQueries })

  return organisationAssignmentQueries
}

const fetchAccommodationsByOrgId = async (accommodationIds: number[] | undefined, organisationId: string) => {
  const response = await apis.accommodation.getAccommodationsByListOfIds({
    accommodationIds,
    organisationId,
  })
  if (!response?.data || !Array.isArray(response?.data)) return []
  response.data.sort((a, b) => {
    if (a.name < b.name) return -1
    if (a.name > b.name) return 1
    return 0
  })
  return response.data
}

export function useAccommodationsByOrgId(orgId: ComputedRef<string | null | undefined>) {
  const { assignmentsData } = useOrganisationAssignments(orgId)
  const queryClient = useQueryClient()

  const isQueryEnabled = computed(() => {
    return !!assignmentsData?.value && assignmentsData?.value?.length > 0
  })

  const { isLoading, data, isFetching, isSuccess, isError, fetchStatus } = useQuery({
    queryKey: ['organisationAccommodations', orgId],
    enabled: isQueryEnabled,
    queryFn: async () => {
      const organisationId = orgId.value
      if (!organisationId) return []

      const orgAssignmentsQueryData = queryClient.getQueryData<number[]>(['organisationAssignments', orgId])
      return await fetchAccommodationsByOrgId(orgAssignmentsQueryData, organisationId)
    },
  })

  const currentAccommodation = useCurrentAccommodation(data)
  const isInitialLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')

  return {
    isAccommodationsByOrgIdInitialLoading: isInitialLoading,
    isAccommodationsByOrgIdFetching: isFetching,
    isAccommodationsByOrgIdLoading: isLoading,
    isAccommodationsByOrgIdSuccess: isSuccess,
    isAccommodationsByOrgIdError: isError,
    accommodationsData: data,
    currentAccommodation,
  }
}

export function useMultipleAccommodationsByOrgIdQueries(organisations: ComputedRef<Organisation[] | undefined>) {
  const queryClient = useQueryClient()

  const accommodationQueries = computed(() =>
    organisations?.value?.map(org => {
      return {
        queryKey: ['organisationAccommodations', org.id],
        queryFn: async () => {
          const orgAssignmentsQueryData = await queryClient.ensureQueryData({
            queryKey: ['organisationAssignments', org.id],
            queryFn: () => fetchOrganisationAssignments(org.id),
          })
          if (!Array.isArray(orgAssignmentsQueryData) || orgAssignmentsQueryData.length === 0) return []
          const response = await fetchAccommodationsByOrgId(orgAssignmentsQueryData, org.id)
          return response
        },
      }
    })
  )
  const organisationAccommodationsQueries = useQueries({ queries: accommodationQueries })

  return organisationAccommodationsQueries
}
